import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { BacklitAwningForm } from "@/components/BacklitAwningForm";
import heroPicture from "@/assets/backlit-awning-blue-storefront-dusk.png?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import shareImage from "@/assets/backlit-awning-blue-storefront-dusk.png";

export const Route = createFileRoute("/backlit-awnings")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Backlit Awnings — Illuminated Storefront & Sign Awnings in Texas | Texas Made Shade",
      description: "Custom backlit awnings and illuminated sign canopies in Texas — built with Cooley-Brite® translucent fabric on welded aluminum frames.",
      image: shareImage,
      imageAlt: "Custom illuminated backlit awning glowing over a Texas storefront at dusk",
      socialTitle: "Backlit Awnings — Illuminated Storefront & Sign Canopies | Texas Made Shade",
      socialDescription: "Cooley-Brite translucent fabric backlit awnings — welded aluminum frames, engineered for Texas. Built in Seguin, installed across Central & South Texas.",
      keywords: "backlit awnings Texas, illuminated awnings San Antonio, backlit storefront awnings Austin, Cooley-Brite awnings, sign awnings Texas, backlit canopy, illuminated commercial awnings, backlit fabric awnings, glowing storefront awnings",
    }),
    scripts: serviceHeadScripts({
      path: "/backlit-awnings",
      serviceName: "Backlit Awnings",
      breadcrumbName: "Backlit Awnings",
      description: "Custom backlit awnings and illuminated sign canopies using Cooley-Brite translucent fabric on welded aluminum frames — built for restaurants.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/backlit-awnings"
      serviceName="Backlit Awnings"
      eyebrow="★ BACKLIT AWNINGS ★"
      title="Backlit Awnings — Illuminated Storefront & Sign Canopies"
      intro="Custom backlit awnings and illuminated sign canopies built in our Seguin, TX shop using Cooley-Brite® translucent fabric on welded aluminum frames. Designed to glow from within — perfect for restaurants, retail storefronts, and commercial entries that need to stand out after dark across San Antonio, Austin, New Braunfels and Central Texas."
      heroImage={heroPicture}
      heroImageClassName="opacity-80"
      heroOverlayClassName="bg-gradient-to-r from-navy-deep/80 via-navy-deep/40 to-transparent"
      features={[
        "Cooley-Brite® II with Coolthane® EPS — 10-year warranty",
        "20 oz translucent backlit fabric, 20 standard colors",
        "TIG-welded aluminum frame — no prefab kits",
        "Eradicable ink surface for custom graphics & logos",
        "Mildew resistant, UV stable, dielectrically weldable",
        "Engineered for Texas wind and weather",
      ]}
      hideLetsTalkForm
      hideRelatedLinks
      preContent={<BacklitAwningForm />}
      body={[
        {
          heading: "What Are Backlit Awnings?",
          text: "Backlit awnings use translucent fabric — Cooley-Brite® II with Coolthane® EPS — that allows internal LED lighting to shine through, turning your storefront awning into a glowing sign that attracts customers after sunset. The fabric diffuses light evenly for brilliant, high-color graphics and branding that is visible day and night.",
        },
        {
          heading: "Cooley-Brite® II with Coolthane® EPS — The Only Fabric We Use",
          text: "Every backlit awning we build uses Cooley-Brite® II — a 20 oz translucent polyester substrate finished with the Coolthane® Enhanced Protection System top coat. Coolthane® EPS extends fabric life, resists soiling and weathering, and makes routine cleaning easy. The wick-resistant fabric is dielectrically weldable, soft, flexible, and craze resistant. It is mildew resistant, heat sealable, and water repellent, with an eradicable ink top surface that accepts custom graphics, logos, and lettering. Cooley-Brite® II carries a 10-year limited warranty; the lighter 16 oz Cooley-Brite® Lite option carries an 8-year warranty.",
        },
        {
          heading: "Cooley-Brite® II — Standard Color Range",
          text: "Choose from twenty standard 20 oz translucent colors: White 2146, Ivory 2037, Yellow 0123, Sunflower Yellow 2119, Orange 2662, Light Red 0132, Bright Red 2283, Dark Red 2020, Burgundy 0219, Pink 0248, Plum 2025M, Black 2648, Light Blue 0301, Mid Blue 5025, Reflex Blue 2114, Dark Blue 5019, Teal 0355, Light Green 2108, Green 4048, and Forest Green II. We bring physical swatches to your on-site consultation so you can see exactly how each color looks lit and unlit.",
        },
        {
          heading: "Where Backlit Awnings Work Best",
          text: "Restaurants, bars, and retail storefronts that rely on evening foot traffic benefit most from backlit awnings. They serve as both weather protection and a prominent illuminated sign — combining function with marketing in one custom-built structure. Hotels, theaters, and entertainment venues across Texas also use Cooley-Brite® II backlit canopies to create an inviting entrance after dark.",
        },
        {
          heading: "Custom Graphics & Branding",
          text: "Because Cooley-Brite® II features an eradicable ink top coat, we can apply custom graphics, logos, and lettering that glow uniformly when backlit. Your brand becomes a beacon — visible from down the street and memorable to passersby. We pattern, print, and fabricate everything in-house at our Seguin shop.",
        },
        {
          heading: "Frame & Installation",
          text: "Every backlit awning starts with a TIG-welded aluminum frame built to the exact dimensions of your storefront. We engineer the internal LED lighting channel into the frame itself — clean, hidden, and serviceable — then stretch the Cooley-Brite® II fabric tight for a crisp, professional appearance that holds up to Texas wind and rain.",
        },
      ]}
      applications={["Restaurants & Bars", "Retail Storefronts", "Hotels & Theaters", "Commercial Entries", "Entertainment Venues", "Downtown Main Street"]}
      faqs={[
        {
          q: "How long do backlit awnings last in Texas?",
          a: "Cooley-Brite II backlit fabric carries a 10-year warranty and is highly resistant to mildew and UV rays. Our welded aluminum frames are built to last 20+ years. The internal LED lighting systems we install typically last 50,000+ hours — years of nightly operation before any maintenance is needed.",
        },
        {
          q: "Can you put my logo or business name on a backlit awning?",
          a: "Yes. The eradicable ink surface on Cooley-Brite fabric is designed for custom graphics, logos, and lettering that illuminate evenly from behind. We handle the design, layout, and fabrication in our Seguin shop, then install the finished awning with your branding glowing brightly.",
        },
        {
          q: "What colors are available for backlit awning fabric?",
          a: "Cooley-Brite backlit fabric is available in a wide range of standard colors, with custom color matching available for large orders. The fabric shown here is Orange C2119A — a vivid, high-visibility hue that pops when illuminated. We bring swatches to your on-site consultation so you can see how colors look lit and unlit.",
        },
        {
          q: "Do backlit awnings meet Texas fire codes?",
          a: "Yes. Cooley-Brite fabric meets ASTM E84 (Class A), California State Fire Marshal, NFPA 701, and UL Listed fire safety standards — making it compliant for commercial installations across Texas including San Antonio, Austin, and New Braunfels.",
        },
        {
          q: "How wide can a backlit awning be?",
          a: "Cooley-Brite II fabric is 78 inches wide. For awnings wider than that, we seam the fabric using dielectric welding or heat sealing — the same methods used in commercial tent and canopy manufacturing — so the seams are strong, waterproof, and nearly invisible when backlit. We have built backlit awnings from 6 ft up to 40+ ft wide.",
        },
        {
          q: "What is the weight of backlit awning fabric?",
          a: "Cooley-Brite II is a 20-ounce-per-square-yard substrate — substantial enough for durability and light diffusion, yet flexible enough to pattern and install cleanly over welded aluminum frames. The lighter Cooley-Brite Lite option is 16 oz/sq yd with an 8-year warranty.",
        },
        {
          q: "What Texas cities do you install backlit awnings in?",
          a: "We are based in Seguin and install custom backlit awnings across San Antonio, Austin, New Braunfels, San Marcos, Boerne, Kerrville, Fredericksburg, Corpus Christi and the rest of Central & South Texas. On-site measurements and consultations are free within our service area.",
        },
        {
          q: "How do I get a free backlit awning quote?",
          a: "Send measurements, photos of your storefront, and notes about your branding through the form on our homepage, or call +1 (830) 390-9588. We will come back same business day with sizing questions, fabric options, and a free no-pressure estimate. Built in Seguin, installed across Texas.",
        },
      ]}
      testimonials={{
        eyebrow: "Awning Customer Reviews · 5.0 Rating",
        title: "What Backlit Awning Customers Say",
        items: [
          {
            quote: "The backlit awning completely transformed our restaurant storefront. Customers say they can spot us from two blocks away at night.",
            author: "Maria G.",
            location: "San Antonio, TX",
            role: "Restaurant Backlit Awning",
            rating: 5,
          },
          {
            quote: "Our logo glows perfectly through the fabric — crisp, even light with no hot spots. The install crew was professional and the frame is rock solid.",
            author: "James T.",
            location: "Austin, TX",
            role: "Retail Backlit Awning",
            rating: 5,
          },
          {
            quote: "Worth every penny. The awning pays for itself in increased foot traffic after dark. Texas Made Shade nailed the design and install.",
            author: "Robert K.",
            location: "New Braunfels, TX",
            role: "Bar Backlit Canopy",
            rating: 5,
          },
        ],
      }}
    >
    </ServicePage>
  ),
});
